export type WalletSiteConfig = {
  logo: string;
  frontWalletSecondaryLogo: string;
  favicon: string;
  siteMainEmail: string;
  mainUrl: string;
  name: string;
  privacyAndPolicyUrl: string;
  termsAndConditionsUrl: string;
  frontWalletAuthPageColor: string;
  registrationType: string;
  btcManualSellAddress: string;
  ethErc20ManualSellAddress: string;
  trxTrc20ManualSellAddress: string;
  multilanguage: boolean;
  multiCompanyMode: boolean;
  strongPasswordEnabled: boolean;
  optionalTwofa: boolean;
};

export type GetSiteApiResponse = Partial<WalletSiteConfig> & {
  result?: boolean;
  smallLogo?: string;
};

const trim = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

const hasValue = (value: unknown) => trim(value).length > 0;

const isUnreplacedToken = (value: string) =>
  /^#\{REACT_APP_[^}]+\}#$/.test(value);

const envString = (key: string) => {
  const raw = trim(process.env[key]);
  return isUnreplacedToken(raw) ? "" : raw;
};

const pickFirst = (...values: unknown[]) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && !hasValue(value)) continue;
    return value;
  }
  return undefined;
};

const isValidEmail = (value: unknown) =>
  typeof value === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trim(value));

const isValidUrl = (value: unknown) => {
  const normalized = trim(value);
  if (!normalized) return false;
  if (normalized.startsWith("/")) return true;
  try {
    new URL(normalized);
    return true;
  } catch {
    return false;
  }
};

const isValidColor = (value: unknown) =>
  typeof value === "string" &&
  /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(trim(value));

export const getEnvBrandingConfig = () => {
  const config: Partial<WalletSiteConfig> = {};

  const logo = envString("REACT_APP_LOGO");
  const frontWalletSecondaryLogo = envString("REACT_APP_SMALL_LOGO");
  const favicon = envString("REACT_APP_FAVICON_LOGO");
  const siteMainEmail = envString("REACT_APP_SITE_EMAIL");
  const mainUrl = envString("REACT_APP_SITE_URL");
  const name = envString("REACT_APP_SITE_NAME");
  const privacyAndPolicyUrl = envString("REACT_APP_PRIVACY_POLICY_URL");
  const termsAndConditionsUrl = envString("REACT_APP_TERMS_URL");
  const frontWalletAuthPageColor = envString("REACT_APP_AUTH_PAGE_COLOR");

  if (hasValue(logo)) config.logo = logo;
  if (hasValue(frontWalletSecondaryLogo)) {
    config.frontWalletSecondaryLogo = frontWalletSecondaryLogo;
  }
  if (hasValue(favicon)) config.favicon = favicon;
  if (isValidEmail(siteMainEmail)) config.siteMainEmail = siteMainEmail;
  if (isValidUrl(mainUrl)) config.mainUrl = mainUrl;
  if (hasValue(name)) config.name = name;
  if (isValidUrl(privacyAndPolicyUrl)) {
    config.privacyAndPolicyUrl = privacyAndPolicyUrl;
  }
  if (isValidUrl(termsAndConditionsUrl)) {
    config.termsAndConditionsUrl = termsAndConditionsUrl;
  }
  if (isValidColor(frontWalletAuthPageColor)) {
    config.frontWalletAuthPageColor = frontWalletAuthPageColor;
  }

  return config;
};

export const hasEnvBranding = () => {
  const env = getEnvBrandingConfig();
  return !!(env.logo || env.frontWalletSecondaryLogo);
};

export const resolveLogoUrl = (path: string) => {
  const normalized = trim(path);
  if (!normalized) return "";
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const assetPath = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return `${base}${assetPath}`;
};

const DYNAMIC_DEFAULTS: Pick<
  WalletSiteConfig,
  | "registrationType"
  | "btcManualSellAddress"
  | "ethErc20ManualSellAddress"
  | "trxTrc20ManualSellAddress"
  | "multilanguage"
  | "multiCompanyMode"
  | "strongPasswordEnabled"
  | "optionalTwofa"
  | "privacyAndPolicyUrl"
  | "termsAndConditionsUrl"
  | "frontWalletAuthPageColor"
> = {
  registrationType: "extended",
  btcManualSellAddress: "",
  ethErc20ManualSellAddress: "",
  trxTrc20ManualSellAddress: "",
  multilanguage: false,
  multiCompanyMode: false,
  strongPasswordEnabled: false,
  optionalTwofa: false,
  privacyAndPolicyUrl: "/privacy-and-policy",
  termsAndConditionsUrl: "/terms-and-conditions",
  frontWalletAuthPageColor: "#111111",
};

export const mergeSiteConfig = (
  apiSite: GetSiteApiResponse = {}
): WalletSiteConfig => {
  const env = getEnvBrandingConfig();

  return {
    logo: (pickFirst(env.logo, apiSite.logo, "") as string) ?? "",
    frontWalletSecondaryLogo:
      (pickFirst(
        env.frontWalletSecondaryLogo,
        apiSite.frontWalletSecondaryLogo,
        apiSite.smallLogo,
        ""
      ) as string) ?? "",
    favicon:
      (pickFirst(env.favicon, apiSite.favicon, "/favicon.png") as string) ??
      "/favicon.png",
    siteMainEmail:
      (pickFirst(env.siteMainEmail, apiSite.siteMainEmail, "") as string) ?? "",
    mainUrl: (pickFirst(env.mainUrl, apiSite.mainUrl, "") as string) ?? "",
    name: (pickFirst(env.name, apiSite.name, "") as string) ?? "",
    privacyAndPolicyUrl:
      (pickFirst(
        env.privacyAndPolicyUrl,
        apiSite.privacyAndPolicyUrl,
        DYNAMIC_DEFAULTS.privacyAndPolicyUrl
      ) as string) ?? DYNAMIC_DEFAULTS.privacyAndPolicyUrl,
    termsAndConditionsUrl:
      (pickFirst(
        env.termsAndConditionsUrl,
        apiSite.termsAndConditionsUrl,
        DYNAMIC_DEFAULTS.termsAndConditionsUrl
      ) as string) ?? DYNAMIC_DEFAULTS.termsAndConditionsUrl,
    frontWalletAuthPageColor:
      (pickFirst(
        env.frontWalletAuthPageColor,
        apiSite.frontWalletAuthPageColor,
        DYNAMIC_DEFAULTS.frontWalletAuthPageColor
      ) as string) ?? DYNAMIC_DEFAULTS.frontWalletAuthPageColor,
    registrationType:
      apiSite.registrationType ?? DYNAMIC_DEFAULTS.registrationType,
    btcManualSellAddress: apiSite.btcManualSellAddress ?? "",
    ethErc20ManualSellAddress: apiSite.ethErc20ManualSellAddress ?? "",
    trxTrc20ManualSellAddress: apiSite.trxTrc20ManualSellAddress ?? "",
    multilanguage: apiSite.multilanguage ?? DYNAMIC_DEFAULTS.multilanguage,
    multiCompanyMode:
      apiSite.multiCompanyMode ?? DYNAMIC_DEFAULTS.multiCompanyMode,
    strongPasswordEnabled:
      apiSite.strongPasswordEnabled ?? DYNAMIC_DEFAULTS.strongPasswordEnabled,
    optionalTwofa: apiSite.optionalTwofa ?? DYNAMIC_DEFAULTS.optionalTwofa,
  };
};

export const getBootstrapSiteConfig = () => mergeSiteConfig({});
